import { Controller } from "@hotwired/stimulus"

// Client Layout Scores by MSO Group (card #2294, WIN Article #181 / Document
// #2448). Ported from the confirmed-good OpsAI prototype (v3) -- same
// grouping/bucket/sort/hide logic, adapted to WIN's data shape and Rails
// routes. The server ships one client's full dataset via the JSON <script>
// payload (see #connect); from there this controller owns ALL interactive
// state (grouping, sorting, hide/restore, toggles, expand/collapse) with zero
// further round-trips -- only a Client change re-fetches (see #clientChanged).
//
// Deliberately builds table markup via template strings rather than Turbo
// Streams -- the whole point of the JSON-payload architecture (per the brief
// and the build plan) is that State/Rank filtering, sorting, hide/restore,
// the two toggles, and expand/collapse all re-render instantly from data
// already in the browser, with no server round trip. A Turbo Stream fetch
// per interaction would defeat that.
export default class extends Controller {
  static targets = [
    "clientSelect", "stateSelect", "rankFrom", "rankTo",
    "detailToggle", "combineToggle", "hideUnscoredToggle",
    "hiddenStrip", "hiddenChips",
    "resultCount", "legend", "headerRow", "tbody",
    "payload"
  ]

  static values = {
    dataUrl: String,
    selectedClientId: String
  }

  static NUMERIC_SORT_KEYS = new Set(["stores", "rank", "placeId"])

  connect() {
    this.loadPayloadData(this.readPayload())
    this.state = {
      stateFilter: "All",
      rankFrom: null,
      rankTo: null,
      hiddenGroups: new Set(),
      hiddenCategories: new Set(),
      hiddenStores: new Set(),
      expandedGroups: new Set(),
      allExpanded: false,
      sortKey: "name",
      sortDir: "asc",
      combineGoodWinning: false,
      hideUnscoredFromBar: false
    }

    this.render()
  }

  // ---------- data loading ----------

  readPayload() {
    if (!this.hasPayloadTarget) return { stores: [], categories: [], scores: [] }

    const raw = this.payloadTarget.textContent
    if (!raw || !raw.trim()) return { stores: [], categories: [], scores: [] }

    return JSON.parse(raw)
  }

  loadPayloadData(data) {
    this.reportData = data || { stores: [], categories: [], scores: [] }
    // Categories are the full set the client has any client_layout_scores
    // row for (from the server), independent of the current State/Rank
    // filters -- the brief's "categories shown are whatever the selected
    // client has scored data for", not a per-filter recomputation.
    this.categoriesAll = this.reportData.categories || []
    this.scoreMap = this.buildScoreMap(this.reportData.scores || [])
    this.scoreIdMap = this.buildScoreIdMap(this.reportData.scores || [])
    this.placeNameById = new Map((this.reportData.stores || []).map(s => [s.place_id, s.place_name]))
  }

  buildScoreMap(scores) {
    const map = new Map()
    scores.forEach(s => map.set(`${s.place_id}:${s.category_id}`, s.score))
    return map
  }

  // Separate lookup for score_id -- keeps scoreMap returning a raw score
  // number everywhere it's already relied on (categoryCounts, sortValue,
  // bucket rendering), and adds click-through support (card #2359) only
  // where a link is actually rendered.
  buildScoreIdMap(scores) {
    const map = new Map()
    scores.forEach(s => map.set(`${s.place_id}:${s.category_id}`, s.score_id))
    return map
  }

  // ---------- filter bar actions ----------

  // Only a Client change re-fetches from the server (mirrors
  // indie_cut_in_report_controller's #clientChanged fetch pattern). Clears
  // all per-client hidden/expanded state and the master detail toggle, but
  // deliberately leaves Combine/Hide-Unscored untouched -- those are global
  // display preferences, not per-client filter state (brief: Filters >
  // Client).
  clientChanged(event) {
    const clientId = event.target.value
    this.selectedClientIdValue = clientId

    fetch(`${this.dataUrlValue}?client_id=${encodeURIComponent(clientId)}`, {
      headers: { "Accept": "application/json" }
    })
      .then(response => response.json())
      .then(data => {
        this.loadPayloadData(data)

        this.state.stateFilter = "All"
        this.state.rankFrom = null
        this.state.rankTo = null
        this.state.hiddenGroups.clear()
        this.state.hiddenCategories.clear()
        this.state.hiddenStores.clear()
        this.state.expandedGroups.clear()
        this.state.allExpanded = false

        if (this.hasStateSelectTarget) this.stateSelectTarget.value = "All"
        if (this.hasRankFromTarget) this.rankFromTarget.value = ""
        if (this.hasRankToTarget) this.rankToTarget.value = ""
        if (this.hasDetailToggleTarget) {
          this.detailToggleTarget.textContent = "Show Store Detail"
          this.detailToggleTarget.classList.remove("active")
        }

        this.render()
      })
  }

  // State select + Rank From/To inputs -- applied client-side against the
  // already-loaded payload, no round trip.
  refresh() {
    if (this.hasStateSelectTarget) this.state.stateFilter = this.stateSelectTarget.value
    if (this.hasRankFromTarget) {
      this.state.rankFrom = this.rankFromTarget.value === "" ? null : Number(this.rankFromTarget.value)
    }
    if (this.hasRankToTarget) {
      this.state.rankTo = this.rankToTarget.value === "" ? null : Number(this.rankToTarget.value)
    }
    this.render()
  }

  toggleDetail() {
    this.state.allExpanded = !this.state.allExpanded

    if (this.state.allExpanded) {
      if (this.hasDetailToggleTarget) {
        this.detailToggleTarget.textContent = "Hide Store Detail"
        this.detailToggleTarget.classList.add("active")
      }
      this.groupsForClient().forEach(g => this.state.expandedGroups.add(g.key))
    } else {
      if (this.hasDetailToggleTarget) {
        this.detailToggleTarget.textContent = "Show Store Detail"
        this.detailToggleTarget.classList.remove("active")
      }
      this.state.expandedGroups.clear()
    }

    this.render()
  }

  toggleCombine() {
    this.state.combineGoodWinning = !this.state.combineGoodWinning
    if (this.hasCombineToggleTarget) {
      this.combineToggleTarget.classList.toggle("active", this.state.combineGoodWinning)
    }
    this.render()
  }

  toggleHideUnscored() {
    this.state.hideUnscoredFromBar = !this.state.hideUnscoredFromBar
    if (this.hasHideUnscoredToggleTarget) {
      this.hideUnscoredToggleTarget.textContent = this.state.hideUnscoredFromBar ? "Show Unscored" : "Hide Unscored"
      this.hideUnscoredToggleTarget.classList.toggle("active", this.state.hideUnscoredFromBar)
    }
    this.render()
  }

  restoreAll() {
    this.state.hiddenGroups.clear()
    this.state.hiddenCategories.clear()
    this.state.hiddenStores.clear()
    this.render()
  }

  // ---------- filtering / grouping ----------

  filteredStores() {
    const stores = this.reportData.stores || []
    return stores.filter(s => {
      if (this.state.hiddenStores.has(s.place_id)) return false
      if (this.state.stateFilter !== "All" && s.state !== this.state.stateFilter) return false

      if (this.state.rankFrom !== null || this.state.rankTo !== null) {
        if (s.place_priority === null || s.place_priority === undefined) return false
        if (this.state.rankFrom !== null && s.place_priority < this.state.rankFrom) return false
        if (this.state.rankTo !== null && s.place_priority > this.state.rankTo) return false
      }

      return true
    })
  }

  // Groups by place_owner_id (null -> synthetic "Independent (No MSO)"
  // group). Only groups with >=3 candidate stores (after State/Rank/hidden
  // filters) survive -- a hard cutoff evaluated fresh on every render, per
  // Article #181's Grouping Logic. Lowered from >=5 to >=3 on 2026-07-15 per
  // Ryan (card #2359) -- Morgans (place_owner id 35) has exactly 4 stores and
  // never cleared the old 5-store bar.
  groupsForClient() {
    const stores = this.filteredStores()
    const map = new Map()

    for (const s of stores) {
      const hasOwner = s.place_owner_id !== null && s.place_owner_id !== undefined
      const key = hasOwner ? String(s.place_owner_id) : "none"
      const name = hasOwner ? s.place_owner_name : "Independent (No MSO)"

      if (!map.has(key)) map.set(key, { key, name, ownerId: hasOwner ? s.place_owner_id : null, stores: new Map() })
      map.get(key).stores.set(s.place_id, s)
    }

    const groups = [...map.values()].filter(g => g.stores.size >= 3)

    for (const g of groups) {
      const memberStores = [...g.stores.values()]
      g.states = [...new Set(memberStores.map(s => s.state).filter(Boolean))].sort()
      const ranked = memberStores.filter(s => s.place_priority !== null && s.place_priority !== undefined)
      g.avgRank = ranked.length ? Math.round(ranked.reduce((sum, s) => sum + s.place_priority, 0) / ranked.length) : null
    }

    return groups
  }

  visibleCategories() {
    return this.categoriesAll.filter(c => !this.state.hiddenCategories.has(String(c.id)))
  }

  // ---------- bucket logic (CRITICAL -- see Article #181) ----------

  // A store with no scoreMap entry for this category (never scored, or no
  // client_layout_scores row at all) counts as Not Scored identically to a
  // row with a null score -- this is the corrected v3 behaviour the report
  // exists to guarantee. Do not special-case "no record" differently.
  bucketForScore(score) {
    if (score === null || score === undefined) return "Not Scored"
    if (score >= 75) return "Winning"
    if (score >= 50) return "Good"
    return "Room for Improvement"
  }

  // Iterates every store currently in the group -- not just stores with an
  // existing client_layout_scores row -- so a lookup-miss correctly counts
  // as Not Scored rather than being silently dropped.
  categoryCounts(group, categoryId) {
    const counts = { Winning: 0, Good: 0, "Room for Improvement": 0, "Not Scored": 0 }
    for (const s of group.stores.values()) {
      const score = this.scoreMap.get(`${s.place_id}:${categoryId}`)
      counts[this.bucketForScore(score)]++
    }
    return counts
  }

  bucketClass(bucket) {
    if (this.state.combineGoodWinning && (bucket === "Winning" || bucket === "Good")) return "clms-winning"
    if (bucket === "Winning") return "clms-winning"
    if (bucket === "Good") return "clms-good"
    if (bucket === "Room for Improvement") return "clms-room"
    return "clms-notscored"
  }

  // Mirrors the star/thumbs-up/thumbs-down convention used in
  // client_layout_scores/_score_icons_inline.html.erb (folded into card #2359).
  bucketIcon(bucket) {
    if (bucket === "Winning") return "⭐"
    if (bucket === "Good") return "👍"
    if (bucket === "Room for Improvement") return "👎"
    return "–"
  }

  sumCounts(a, b) {
    return {
      Winning: a.Winning + b.Winning,
      Good: a.Good + b.Good,
      "Room for Improvement": a["Room for Improvement"] + b["Room for Improvement"],
      "Not Scored": a["Not Scored"] + b["Not Scored"]
    }
  }

  // ---------- sorting (two independent levels, same key/direction) ----------

  isCatKey(key) { return typeof key === "string" && key.startsWith("cat_") }
  catIdFromKey(key) { return Number(key.slice(4)) }

  groupSortValue(g, key) {
    if (key === "name") return g.name.toLowerCase()
    if (key === "state") return g.states.join(",")
    if (key === "stores") return g.stores.size
    if (key === "rank") return g.avgRank === null ? Infinity : g.avgRank
    if (key === "placeId") return g.name.toLowerCase()
    if (this.isCatKey(key)) {
      const counts = this.categoryCounts(g, this.catIdFromKey(key))
      return counts.Winning * 1000 + counts.Good * 10 + counts["Room for Improvement"]
    }
    return g.name.toLowerCase()
  }

  storeSortValue(s, key) {
    if (key === "name") return s.place_name.toLowerCase()
    if (key === "state") return (s.state || "").toLowerCase()
    if (key === "stores") return s.place_name.toLowerCase()
    if (key === "rank") return (s.place_priority === null || s.place_priority === undefined) ? Infinity : s.place_priority
    if (key === "placeId") return s.place_id
    if (this.isCatKey(key)) {
      const score = this.scoreMap.get(`${s.place_id}:${this.catIdFromKey(key)}`)
      return (score === null || score === undefined) ? -1 : score
    }
    return s.place_name.toLowerCase()
  }

  applySort(arr, valueFn) {
    const dir = this.state.sortDir === "asc" ? 1 : -1
    arr.sort((a, b) => {
      const va = valueFn(a)
      const vb = valueFn(b)
      if (va < vb) return -1 * dir
      if (va > vb) return 1 * dir
      return 0
    })
    return arr
  }

  setSortKey(key) {
    if (this.state.sortKey === key) {
      this.state.sortDir = this.state.sortDir === "asc" ? "desc" : "asc"
    } else {
      this.state.sortKey = key
      this.state.sortDir = (this.constructor.NUMERIC_SORT_KEYS.has(key) || this.isCatKey(key)) ? "desc" : "asc"
    }
    this.render()
  }

  // ---------- rendering ----------

  render() {
    if (!this.reportData) return

    const groups = this.groupsForClient()
    const categories = this.visibleCategories()
    this.applySort(groups, g => this.groupSortValue(g, this.state.sortKey))

    const visibleGroups = groups.filter(g => !this.state.hiddenGroups.has(g.key))
    const hiddenGroupsList = groups.filter(g => this.state.hiddenGroups.has(g.key))

    this.renderHeader(categories)
    this.renderLegend()
    this.renderHiddenStrip(hiddenGroupsList, categories)
    const { totalStores, totalScoredLayouts } = this.renderBody(visibleGroups, categories)
    this.renderResultCount(visibleGroups.length, totalStores, totalScoredLayouts, categories.length)
  }

  renderHeader(categories) {
    if (!this.hasHeaderRowTarget) return

    const arrow = (key) => this.state.sortKey === key
      ? `<span class="clms-sort-arrow">${this.state.sortDir === "asc" ? "▲" : "▼"}</span>`
      : ""

    this.headerRowTarget.innerHTML = `
      <tr>
        <th class="table-header-primary" style="min-width:26px;"></th>
        <th class="table-header-primary" style="min-width:34px;"></th>
        <th class="table-header-primary clms-sortable" data-sort-key="name">Group / Store${arrow("name")}</th>
        <th class="table-header-primary clms-sortable" data-sort-key="state">State${arrow("state")}</th>
        <th class="table-header-primary clms-sortable text-right" data-sort-key="stores">Stores${arrow("stores")}</th>
        <th class="table-header-primary clms-sortable text-right" data-sort-key="rank">Rank${arrow("rank")}</th>
        <th class="table-header-primary clms-sortable text-right" data-sort-key="placeId">Place ID${arrow("placeId")}</th>
        ${categories.map(c => `
          <th class="table-header-primary clms-sortable text-center" data-sort-key="cat_${c.id}">
            ${c.name}${arrow("cat_" + c.id)}
            <span class="clms-category-hide" data-category-id="${c.id}" title="Hide this category">✕</span>
          </th>
        `).join("")}
      </tr>
    `

    this.headerRowTarget.querySelectorAll("th[data-sort-key]").forEach(th => {
      th.addEventListener("click", () => this.setSortKey(th.dataset.sortKey))
    })
    this.headerRowTarget.querySelectorAll(".clms-category-hide").forEach(btn => {
      btn.addEventListener("click", (event) => {
        event.stopPropagation()
        this.state.hiddenCategories.add(btn.dataset.categoryId)
        this.render()
      })
    })
  }

  renderLegend() {
    if (!this.hasLegendTarget) return

    this.legendTarget.innerHTML = this.state.combineGoodWinning
      ? `<span class="flex items-center gap-1"><span class="inline-block w-3 h-3 rounded" style="background:#22c55e;"></span>Good (Winning + Good combined)</span>
         <span class="flex items-center gap-1"><span class="inline-block w-3 h-3 rounded" style="background:#f59e0b;"></span>Room for Improvement</span>
         <span class="flex items-center gap-1"><span class="inline-block w-3 h-3 rounded" style="background:#cbd5e1;"></span>Not Scored</span>`
      : `<span class="flex items-center gap-1"><span class="inline-block w-3 h-3 rounded" style="background:#22c55e;"></span>Winning</span>
         <span class="flex items-center gap-1"><span class="inline-block w-3 h-3 rounded" style="background:#60a5fa;"></span>Good</span>
         <span class="flex items-center gap-1"><span class="inline-block w-3 h-3 rounded" style="background:#f59e0b;"></span>Room for Improvement</span>
         <span class="flex items-center gap-1"><span class="inline-block w-3 h-3 rounded" style="background:#cbd5e1;"></span>Not Scored</span>`
  }

  renderHiddenStrip(hiddenGroupsList, categories) {
    if (!this.hasHiddenStripTarget || !this.hasHiddenChipsTarget) return

    const hiddenCatObjs = this.categoriesAll.filter(c => this.state.hiddenCategories.has(String(c.id)))
    const hiddenStoreIds = [...this.state.hiddenStores]
    const anyHidden = hiddenGroupsList.length || hiddenCatObjs.length || hiddenStoreIds.length

    this.hiddenStripTarget.classList.toggle("hidden", !anyHidden)
    if (!anyHidden) return

    this.hiddenChipsTarget.innerHTML =
      hiddenGroupsList.map(g => `
        <span class="clms-hidden-chip">Group: ${g.name} (${g.stores.size})
          <button type="button" data-restore-type="group" data-restore-key="${g.key}">×</button>
        </span>`).join("")
      + hiddenCatObjs.map(c => `
        <span class="clms-hidden-chip">Category: ${c.name}
          <button type="button" data-restore-type="category" data-restore-key="${c.id}">×</button>
        </span>`).join("")
      + hiddenStoreIds.map(pid => `
        <span class="clms-hidden-chip">Store: ${this.placeNameById.get(pid) || ("#" + pid)}
          <button type="button" data-restore-type="store" data-restore-key="${pid}">×</button>
        </span>`).join("")

    this.hiddenChipsTarget.querySelectorAll("[data-restore-type]").forEach(btn => {
      btn.addEventListener("click", () => {
        const type = btn.dataset.restoreType
        if (type === "group") this.state.hiddenGroups.delete(btn.dataset.restoreKey)
        else if (type === "category") this.state.hiddenCategories.delete(btn.dataset.restoreKey)
        else if (type === "store") this.state.hiddenStores.delete(Number(btn.dataset.restoreKey))
        this.render()
      })
    })
  }

  // Shared fat-bar + counts-line markup for a group row or the Grand Total
  // row (brief: "built from the same shared rendering function").
  buildCatCellHtml(counts) {
    const scoredTotal = counts.Winning + counts.Good + counts["Room for Improvement"]
    const fullTotal = scoredTotal + counts["Not Scored"]
    const barTotal = this.state.hideUnscoredFromBar ? scoredTotal : fullTotal
    const pct = (n) => barTotal ? (n / barTotal * 100) : 0

    const segs = this.state.combineGoodWinning
      ? [
          { seg: "clms-seg-winning", cnt: "clms-cnt-winning", count: counts.Winning + counts.Good },
          { seg: "clms-seg-room", cnt: "clms-cnt-room", count: counts["Room for Improvement"] }
        ]
      : [
          { seg: "clms-seg-winning", cnt: "clms-cnt-winning", count: counts.Winning },
          { seg: "clms-seg-good", cnt: "clms-cnt-good", count: counts.Good },
          { seg: "clms-seg-room", cnt: "clms-cnt-room", count: counts["Room for Improvement"] }
        ]

    const barSegs = segs.map(s => `<span class="${s.seg}" style="width:${pct(s.count)}%"></span>`).join("")
      + (!this.state.hideUnscoredFromBar && counts["Not Scored"]
          ? `<span class="clms-seg-notscored" style="width:${pct(counts["Not Scored"])}%"></span>`
          : "")

    const countLine = segs.map(s => `<span class="${s.count ? s.cnt : "clms-cnt-dash"}">${s.count || "–"}</span>`).join("")
      + (counts["Not Scored"] ? `<span class="clms-cnt-notscored">${counts["Not Scored"]}</span>` : "")

    return `<div class="clms-fat-bar">${barSegs}</div><div class="clms-counts-line">${countLine}</div>`
  }

  renderBody(visibleGroups, categories) {
    if (!this.hasTbodyTarget) return { totalStores: 0, totalScoredLayouts: 0 }

    let bodyHtml = ""
    let totalStores = 0
    let totalScoredLayouts = 0
    let grandTotalCatCounts = categories.map(() => ({ Winning: 0, Good: 0, "Room for Improvement": 0, "Not Scored": 0 }))

    for (const g of visibleGroups) {
      totalStores += g.stores.size
      const expanded = this.state.expandedGroups.has(g.key)

      const groupCatCounts = categories.map(c => this.categoryCounts(g, c.id))
      const catCells = categories.map((c, i) => {
        const counts = groupCatCounts[i]
        totalScoredLayouts += (counts.Winning + counts.Good + counts["Room for Improvement"])
        return `<td class="table-row-primary text-center">${this.buildCatCellHtml(counts)}</td>`
      }).join("")
      categories.forEach((c, i) => { grandTotalCatCounts[i] = this.sumCounts(grandTotalCatCounts[i], groupCatCounts[i]) })

      const groupNameCell = g.ownerId
        ? `<a href="/place_owners/${g.ownerId}" target="_blank" class="text-indigo-900 hover:underline">${g.name}</a>`
        : g.name

      bodyHtml += `
        <tr class="clms-group-row">
          <td class="table-row-primary"><input type="checkbox" class="clms-hide-group" data-group-key="${g.key}" title="Hide this group"></td>
          <td class="table-row-primary"><button type="button" class="clms-expand-btn" data-group-key="${g.key}">${expanded ? "▾" : "▸"}</button></td>
          <td class="table-row-primary font-semibold">${groupNameCell}</td>
          <td class="table-row-primary"><div class="flex flex-wrap gap-1">${g.states.map(s => `<span class="clms-state-pill">${s}</span>`).join("")}</div></td>
          <td class="table-row-primary text-right">${g.stores.size}</td>
          <td class="table-row-primary text-right">${g.avgRank !== null ? "avg #" + g.avgRank : "—"}</td>
          <td class="table-row-primary text-right"></td>
          ${catCells}
        </tr>
      `

      if (expanded) {
        const stores = [...g.stores.values()]
        this.applySort(stores, s => this.storeSortValue(s, this.state.sortKey))

        for (const s of stores) {
          const storeCatCells = categories.map(c => {
            const score = this.scoreMap.get(`${s.place_id}:${c.id}`)
            const hasScore = score !== null && score !== undefined
            const bucket = this.bucketForScore(score)
            const cls = hasScore ? this.bucketClass(bucket) : "clms-notscored"
            const icon = hasScore ? this.bucketIcon(bucket) : "–"
            const pillHtml = `<span class="clms-cell-pill ${cls}">${icon}</span>`

            if (!hasScore) return `<td class="table-row-primary text-center">${pillHtml}</td>`

            const scoreId = this.scoreIdMap.get(`${s.place_id}:${c.id}`)
            return `<td class="table-row-primary text-center"><a href="/client_layout_scores/${scoreId}" target="_blank">${pillHtml}</a></td>`
          }).join("")

          bodyHtml += `
            <tr class="clms-store-row">
              <td class="table-row-primary"><input type="checkbox" class="clms-hide-store" data-place-id="${s.place_id}" title="Hide this store"></td>
              <td class="table-row-primary"></td>
              <td class="table-row-primary pl-6"><a href="/places/${s.place_id}" target="_blank" class="text-gray-800 hover:underline">${s.place_name}</a></td>
              <td class="table-row-primary">${s.state ? `<span class="clms-state-pill">${s.state}</span>` : ""}</td>
              <td class="table-row-primary"></td>
              <td class="table-row-primary text-right">${s.place_priority !== null && s.place_priority !== undefined ? "#" + s.place_priority : "—"}</td>
              <td class="table-row-primary text-right font-mono text-gray-400">${s.place_id}</td>
              ${storeCatCells}
            </tr>
          `
        }
      }
    }

    // Grand Total row -- pinned as the first row, aggregated across every
    // currently visible group. Renders nothing at all when there are zero
    // visible groups (brief: Grand Total Row).
    let grandTotalHtml = ""
    if (visibleGroups.length && categories.length) {
      let rankSum = 0
      let rankCount = 0
      for (const g of visibleGroups) {
        for (const s of g.stores.values()) {
          if (s.place_priority !== null && s.place_priority !== undefined) {
            rankSum += s.place_priority
            rankCount++
          }
        }
      }
      const grandAvgRank = rankCount ? Math.round(rankSum / rankCount) : null
      const grandCatCells = categories.map((c, i) => `<td class="table-row-primary text-center">${this.buildCatCellHtml(grandTotalCatCounts[i])}</td>`).join("")

      grandTotalHtml = `
        <tr class="clms-grand-total-row">
          <td class="table-row-primary"></td>
          <td class="table-row-primary"></td>
          <td class="table-row-primary font-bold">Grand Total</td>
          <td class="table-row-primary"></td>
          <td class="table-row-primary text-right">${totalStores}</td>
          <td class="table-row-primary text-right">${grandAvgRank !== null ? "avg #" + grandAvgRank : "—"}</td>
          <td class="table-row-primary text-right"></td>
          ${grandCatCells}
        </tr>
      `
    }

    this.tbodyTarget.innerHTML = grandTotalHtml + bodyHtml

    this.tbodyTarget.querySelectorAll(".clms-hide-group").forEach(cb => {
      cb.addEventListener("click", () => {
        this.state.hiddenGroups.add(cb.dataset.groupKey)
        this.render()
      })
    })
    this.tbodyTarget.querySelectorAll(".clms-hide-store").forEach(cb => {
      cb.addEventListener("click", () => {
        this.state.hiddenStores.add(Number(cb.dataset.placeId))
        this.render()
      })
    })
    this.tbodyTarget.querySelectorAll(".clms-expand-btn").forEach(btn => {
      btn.addEventListener("click", () => {
        const key = btn.dataset.groupKey
        if (this.state.expandedGroups.has(key)) this.state.expandedGroups.delete(key)
        else this.state.expandedGroups.add(key)
        this.render()
      })
    })

    return { totalStores, totalScoredLayouts }
  }

  renderResultCount(groupCount, totalStores, totalScoredLayouts, categoryCount) {
    if (!this.hasResultCountTarget) return

    if (!this.reportData.stores || this.reportData.stores.length === 0) {
      this.resultCountTarget.textContent = "Select a client to generate the report."
      return
    }

    this.resultCountTarget.textContent =
      `Showing ${groupCount} group${groupCount === 1 ? "" : "s"} · ${totalStores} stores · ${totalScoredLayouts} scored layouts across ${categoryCount} categor${categoryCount === 1 ? "y" : "ies"}`
  }
}
